import React, { useState } from 'react';
import axios from 'axios';
import { BarChart3, FileText, Download, Calendar, TrendingUp, DollarSign, Package, Truck } from 'lucide-react';
import './ReportsTab.css';

const ReportsTab = ({ hotelId, token }) => {
  const [selectedReport, setSelectedReport] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [error, setError] = useState('');

  const reports = [
    {
      id: 'valuation',
      name: 'Inventory Valuation',
      description: 'Current stock value and status',
      icon: DollarSign,
      endpoint: '/api/inventory/reports/valuation'
    },
    {
      id: 'consumption',
      name: 'Department Consumption',
      description: 'Usage by department over time',
      icon: TrendingUp,
      endpoint: '/api/inventory/reports/department-consumption'
    },
    {
      id: 'vendor-performance',
      name: 'Vendor Performance',
      description: 'Supplier reliability and metrics',
      icon: Truck,
      endpoint: '/api/inventory/reports/vendor-performance'
    },
    {
      id: 'wastage',
      name: 'Wastage Report',
      description: 'Items wasted and their costs',
      icon: Package,
      endpoint: '/api/inventory/reports/wastage'
    },
    {
      id: 'cost-analysis',
      name: 'Cost Analysis',
      description: 'Expense breakdown and trends',
      icon: BarChart3,
      endpoint: '/api/inventory/reports/cost-analysis'
    },
    {
      id: 'purchase-analysis',
      name: 'Purchase Analysis',
      description: 'Procurement patterns and history',
      icon: FileText,
      endpoint: '/api/inventory/reports/purchase-analysis'
    }
  ];

  const fetchReport = async (report) => {
    setSelectedReport(report.id);
    setLoading(true);
    setError('');

    try {
      let url = `${process.env.REACT_APP_API_URL}${report.endpoint}?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to generate report');
      setLoading(false);
    }
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    if (Array.isArray(reportData) && reportData.length === 0) {
      return (
        <div className="empty-state">
          <FileText size={48} />
          <h3>No Data</h3>
          <p>No data available for the selected date range</p>
        </div>
      );
    }

    // Generic table renderer for array data
    if (Array.isArray(reportData) && reportData.length > 0) {
      const columns = Object.keys(reportData[0]);
      
      return (
        <div className="report-table-container">
          <table className="report-table">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col}>{col.replace(/_/g, ' ').toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, idx) => (
                <tr key={idx}>
                  {columns.map(col => (
                    <td key={col}>
                      {typeof row[col] === 'number' 
                        ? (col.includes('price') || col.includes('cost') || col.includes('value') 
                            ? `$${parseFloat(row[col]).toFixed(2)}`
                            : col.includes('rate') || col.includes('percentage')
                              ? `${parseFloat(row[col]).toFixed(1)}%`
                              : parseFloat(row[col]).toFixed(2))
                        : row[col] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="info-message">
        Report data structure not supported for display
      </div>
    );
  };

  return (
    <div className="reports-tab">
      <div className="tab-header">
        <h2><BarChart3 size={24} /> Reports & Analytics</h2>
        <div className="date-range-selector">
          <Calendar size={20} />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
          />
          <span>to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="reports-grid">
        {reports.map((report) => {
          const Icon = report.icon;
          const isActive = selectedReport === report.id;
          
          return (
            <div 
              key={report.id}
              className={`report-card ${isActive ? 'active' : ''}`}
              onClick={() => fetchReport(report)}
            >
              <div className="report-icon">
                <Icon size={32} />
              </div>
              <h3>{report.name}</h3>
              <p>{report.description}</p>
              {isActive && (
                <div className="report-badge">
                  <FileText size={14} />
                  Active
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading && (
        <div className="report-loading">
          <div className="loading-spinner"></div>
          <p>Generating report...</p>
        </div>
      )}

      {!loading && reportData && (
        <div className="report-content">
          <div className="report-header">
            <h3>{reports.find(r => r.id === selectedReport)?.name}</h3>
            <button className="btn-secondary" title="Download report">
              <Download size={20} /> Export
            </button>
          </div>
          {renderReportContent()}
        </div>
      )}

      {!selectedReport && !loading && (
        <div className="empty-state">
          <BarChart3 size={48} />
          <h3>Select a Report</h3>
          <p>Choose a report type above to view analytics</p>
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
